// Pure pregnancy-dating math — the single source of truth shared by the backend
// (`src/core/date/gestation.ts`) and the frontend (`frontend/src/lib/gestogram.ts`),
// both of which re-export from here. No React, no I/O, no platform globals beyond
// `Date`.
//
// The calendar arithmetic underneath is `@synapse/calendar`, and this package
// deliberately does NOT re-export it. It used to own those primitives, which made
// every consumer of `addDays` a consumer of pregnancy-dating math — an inventory
// report listing gestation as a dependency. Importing them here and leaving them
// there keeps this package about one subject.
//
// All dates are `yyyy-mm-dd` ISO calendar-date strings; see `@synapse/calendar`
// for why they are strings and not `Date`s.
//
// Naegele's rule: EDD = LMP + 280 days. POA (period of amenorrhoea / gestational
// age) = today − LMP. A pregnancy is "conclusively over" once today is past
// EDD + POSTTERM_GRACE_DAYS (~44 weeks) — far enough past term that any ongoing
// pregnancy must have delivered. That cutoff drives the lazy conclusion in the
// backend `consultationsService` and the carry-forward gate in the frontend
// `seedConsultationPayload`.

import { addDays, diffDays, isValidIsoDate, todayIsoUtc } from "@synapse/calendar";

export const GESTATION_DAYS = 280;
/** Grace past EDD before a pregnancy is treated as conclusively over (~44w total). */
export const POSTTERM_GRACE_DAYS = 28;

/** Naegele's rule: estimated date of delivery = LMP + 280 days. */
export function eddFromLmp(lmpIso: string): string {
  return addDays(lmpIso, GESTATION_DAYS);
}
/** Backend-named alias of {@link eddFromLmp}. */
export const eddFromLmpIso = eddFromLmp;

/** Reverse override: derive the implied LMP from a clinician-set EDD. */
export function lmpFromEdd(eddIso: string): string {
  return addDays(eddIso, -GESTATION_DAYS);
}

/**
 * The EDD in effect for a pregnancy: a valid clinician override wins, else Naegele
 * from the LMP. `lmpIso` must already be a valid ISO date (caller's responsibility).
 */
export function effectiveEdd(lmpIso: string, eddOverrideIso?: string | null): string {
  return isValidIsoDate(eddOverrideIso) ? eddOverrideIso : eddFromLmp(lmpIso);
}
/** Backend-named alias of {@link effectiveEdd}. */
export const effectiveEddIso = effectiveEdd;

export interface Poa {
  weeks: number;
  days: number;
  /** Total elapsed days (clamped at 0); `weeks * 7 + days`. */
  totalDays: number;
}

/**
 * Period of amenorrhoea (gestational age) = today − LMP, split into completed
 * weeks + remaining days. A future LMP (negative elapsed) clamps to 0. `todayIso`
 * is injectable for deterministic tests; defaults to the current UTC calendar date.
 */
export function poaFromLmp(lmpIso: string, todayIso: string = todayIsoUtc()): Poa {
  const elapsed = Math.max(0, diffDays(todayIso, lmpIso));
  return { weeks: Math.floor(elapsed / 7), days: elapsed % 7, totalDays: elapsed };
}

/** Trimester from completed gestational weeks: 1 = 0–13, 2 = 14–27, 3 = 28+. */
export function trimester(weeks: number): 1 | 2 | 3 {
  if (weeks < 14) return 1;
  if (weeks < 28) return 2;
  return 3;
}

/**
 * True when `today` is past the effective EDD — used to flag (not retire) a
 * post-term pregnancy. Returns false for an invalid/missing LMP.
 */
export function isPastEdd(
  lmpIso: string | null | undefined,
  eddOverrideIso?: string | null,
  today: string = todayIsoUtc(),
): boolean {
  if (!isValidIsoDate(lmpIso)) return false;
  return diffDays(today, effectiveEdd(lmpIso, eddOverrideIso)) > 0;
}

/**
 * True when a pregnancy is conclusively over (today past EDD + grace) and should
 * no longer count as the patient's *current* pregnancy. Returns false for an
 * invalid/missing LMP.
 */
export function isConcludable(
  lmpIso: string | null | undefined,
  eddOverrideIso?: string | null,
  today: string = todayIsoUtc(),
): boolean {
  if (!isValidIsoDate(lmpIso)) return false;
  return diffDays(today, effectiveEdd(lmpIso, eddOverrideIso)) > POSTTERM_GRACE_DAYS;
}

/**
 * Backend predicate over an already-resolved effective EDD: true when `todayIso`
 * is strictly past EDD + POSTTERM_GRACE_DAYS. Equivalent to {@link isConcludable}
 * but takes the effective EDD directly (the caller resolved the override). Strict:
 * exactly at the cutoff is NOT yet concludable.
 */
export function isPastConclusionCutoff(effectiveEddIso: string, todayIso: string): boolean {
  // Was a comparison of parsed epoch-ms, which needed the parser this package no
  // longer owns. `diffDays` is the same comparison: both operands are calendar
  // dates, so their difference is a whole number of days and `> 0` is `>`.
  return diffDays(todayIso, addDays(effectiveEddIso, POSTTERM_GRACE_DAYS)) > 0;
}
